import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";

/**
 * ListCard — the shared clickable list row/card used across every portal
 * list (requests, documents, deals, files, notifications). The WHOLE card
 * is one focusable link to the row's natural destination (via a stretched
 * link, so nested action buttons still work), with a consistent
 * title / metadata / status / actions layout and a gentle hover + press
 * state. Motion is CSS-only and respects prefers-reduced-motion.
 *
 * Provide `to` for SPA navigation. Put the status pill in `status` and any
 * overflow / inline actions in `actions` (these sit above the stretched
 * link so they remain independently clickable).
 */
export interface ListCardProps {
  to?: string;
  title: React.ReactNode;
  /** Secondary metadata line(s) under the title (timestamps, counts, etc.). */
  meta?: React.ReactNode;
  /** Right-aligned status pill / badge. */
  status?: React.ReactNode;
  /** Right-aligned actions (overflow menu, buttons) — kept above the link. */
  actions?: React.ReactNode;
  /** Accessible name for the link when the title isn't plain text. */
  linkLabel?: string;
  children?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function ListCard({
  to,
  title,
  meta,
  status,
  actions,
  linkLabel,
  children,
  className,
  "data-testid": testId,
}: ListCardProps) {
  const interactive = to != null;
  return (
    <div
      className={cn(
        "relative flex items-start gap-space-3 rounded-lg border border-border-light bg-white-pure p-space-4 shadow-sm",
        "transition-[box-shadow,transform,border-color] duration-fast ease-standard",
        interactive &&
          "hover:border-gold hover:shadow-md focus-within:border-gold focus-within:shadow-md active:translate-y-px",
        className,
      )}
      data-testid={testId}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-space-2">
          <h3 className="m-0 min-w-0 font-display text-lead font-bold text-onyx">
            {interactive ? (
              // Stretched link — covers the whole card via ::after, while
              // nested buttons (actions) stay clickable because they sit in
              // a higher stacking context below.
              <Link
                to={to!}
                aria-label={linkLabel}
                className="text-onyx no-underline after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:shadow-goldRing rounded-sm"
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </h3>
          {status && <div className="shrink-0">{status}</div>}
        </div>
        {meta && <div className="mt-space-1 text-caption text-gray-mid">{meta}</div>}
        {children && <div className="mt-space-2 text-small text-onyx">{children}</div>}
      </div>
      {actions && (
        // Above the stretched link so clicks land on the action, not the card.
        <div className="relative z-10 shrink-0">{actions}</div>
      )}
    </div>
  );
}
